import * as React from 'react';
import type {} from '@mui/material/themeCssVarsAugmentation';
import { ThemeProvider, createTheme, useColorScheme } from '@mui/material/styles';
import type { ThemeOptions } from '@mui/material/styles';
import { colorSchemes, typography, shadows, shape } from './themePrimitives';
import { useUserPreferences } from '../hooks/Preferences/useUserPreferences';

import i18next from 'i18next';
import { useDispatch } from 'react-redux';
import { setLanguage } from '../store/UI';

interface AppThemeProps {
  children: React.ReactNode;
  /**
   * This is for the docs site. You can ignore it or remove it.
   */
  disableCustomTheme?: boolean;
  themeComponents?: ThemeOptions['components'];
}

function PreferencesSyncer() {
  const dispatch = useDispatch();
  const { setMode } = useColorScheme();
  const { preferences } = useUserPreferences();

  React.useEffect(() => {
    if (preferences?.theme) setMode(preferences.theme);
    if (preferences?.language) {
      i18next.changeLanguage(preferences.language);
      dispatch(setLanguage(preferences.language));
    }
  }, [preferences?.theme, preferences?.language]);

  return null;
}

export default function AppTheme(props: Readonly<AppThemeProps>) {
  const { children, disableCustomTheme, themeComponents } = props;
  const theme = React.useMemo(() => {
    return disableCustomTheme
      ? {}
      : createTheme({
          // For more details about CSS variables configuration, see https://mui.com/material-ui/customization/css-theme-variables/configuration/
          cssVariables: {
            colorSchemeSelector: 'data-mui-color-scheme',
            cssVarPrefix: 'template',
          },
          colorSchemes, // Recently added in v6 for building light & dark mode app, see https://mui.com/material-ui/customization/palette/#color-schemes
          typography,
          shadows,
          shape
        });
  }, [disableCustomTheme, themeComponents]);
  if (disableCustomTheme) {
    return <React.Fragment>{children}</React.Fragment>;
  }
  return (
    <ThemeProvider theme={theme} disableTransitionOnChange>
      <PreferencesSyncer />
      {children}
    </ThemeProvider>
  );
}
